/**
 * PERF-4 — client-perceived import timing (shared across web + native).
 *
 * The device is the only place that knows both the upload *click* and the
 * moment the user learns the job is done. The upload mutation captures
 * `startedAt` (t0) and the resulting jobId; the polling hook sees the COMPLETED
 * transition (the poll that observes status === 'completed', which also drives
 * the "done" UI). This module bridges those two events without prop-drilling,
 * then fires a fire-and-forget telemetry beacon.
 *
 * Keyed by requestId, module-level (one import in flight per request at a time).
 */
import { transcriptionService } from './transcriptionService';

interface ImportTiming {
  startedAt: number; // t0: upload click (mutation start)
  uploadDoneAt: number; // upload response received
  jobId: string;
}

const tracked = new Map<number, ImportTiming>();

/**
 * Called on upload success: associate the captured t0 with the new job so the
 * later COMPLETED transition can compute the total.
 */
export function noteImportUpload(
  requestId: number,
  jobId: string,
  startedAt: number,
): void {
  tracked.set(requestId, { startedAt, uploadDoneAt: Date.now(), jobId });
}

/**
 * Called when a job's status transitions to COMPLETED. If this import is tracked
 * (same session, user stayed on the flow), compute the durations and fire the
 * beacon ONCE. No-op when untracked (page reload, background job, navigated
 * away) — absence is acceptable, never a wrong value.
 */
export function reportImportTimingOnComplete(requestId: number): void {
  const t = tracked.get(requestId);
  if (!t) return;
  tracked.delete(requestId); // once only
  const now = Date.now();
  void transcriptionService.reportClientTiming(t.jobId, {
    clientTotalMs: now - t.startedAt,
    clientUploadMs: t.uploadDoneAt - t.startedAt,
    requestId,
  });
}

/** Test-only: reset tracked state between tests. */
export function __clearImportTimingForTests(): void {
  tracked.clear();
}
